import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { ImageWithFallback } from '../components/media/ImageWithFallback';

function Home() {
    const statsRef = useRef(null);

    useEffect(() => {
        // Animate stats on scroll
        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    entry.target.classList.add('animate-in');
                }
            });
        }, { threshold: 0.3 });

        const stats = document.querySelectorAll('.stat-card');
        stats.forEach(stat => observer.observe(stat));

        return () => observer.disconnect();
    }, []);

    const animateCounter = (element, target) => {
        let current = 0;
        const increment = target / 50;
        const timer = setInterval(() => {
            current += increment;
            if (current >= target) {
                element.textContent = target + '+';
                clearInterval(timer);
            } else {
                element.textContent = Math.floor(current);
            }
        }, 30);
    };

    return (
        <div className="home-page">
            {/* Hero Section */}
            <section className="hero-section">
                <div className="hero-background">
                    <div className="hero-overlay"></div>
                    <div className="animated-shapes">
                        <div className="shape shape-1"></div>
                        <div className="shape shape-2"></div>
                        <div className="shape shape-3"></div>
                    </div>
                </div>

                <div className="container hero-content">
                    <div className="row align-items-center">
                        <div className="col-lg-6 px-4">
                            <h1 className="hero-title animate-slide-up">
                                Locally-Led Solutions For
                                <span className="gradient-text ms-2">Health Improvement & Social Change</span>
                            </h1>
                            <p className="hero-subtitle animate-slide-up delay-1">
                                HSC Foundation serves as Nigeria's first white-label nonprofit platform, enabling individual and organizational donors and philanthropists to implement their own giving ideas through our trusted governance and compliance framework.
                            </p>
                            <div className="hero-buttons animate-slide-up delay-2">
                                <Link to="/programs" className="btn btn-hero-primary">
                                    <Icon icon="carbon:program" className="me-2" />
                                    Explore Programs
                                </Link>
                                {/*
                                <button className="btn btn-hero-secondary">
                                <Icon icon="mdi:hand-heart" className="me-2" />
                                Support Us
                                </button>
                                */}
                            </div>
                            {/*
                            <div className="hero-stats animate-slide-up delay-3">
                                <div className="hero-stat-item">
                                <Icon icon="mdi:account-group" />
                                <div>
                                    <strong>10,000+</strong>
                                    <span>Lives Impacted</span>
                                </div>
                                </div>
                                <div className="hero-stat-item">
                                <Icon icon="mdi:earth" />
                                <div>
                                    <strong>25+</strong>
                                    <span>Communities</span>
                                </div>
                                </div>
                            </div>
                            */}
                        </div>
                        <div className="col-lg-6">
                            <div className="hero-image-wrapper animate-fade-in delay-2">
                                <div className="floating-card card-1">
                                    <Icon icon="mdi:school" />
                                    <span>Education</span>
                                </div>
                                <div className="floating-card card-2">
                                    <Icon icon="mdi:hospital-box" />
                                    <span>Healthcare</span>
                                </div>
                                <div className="floating-card card-3">
                                    <Icon icon="mdi:hand-heart" />
                                    <span>Community</span>
                                </div>
                                <ImageWithFallback
                                    src="https://images.unsplash.com/photo-1553777907-f5dbbbb44d7c?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxhZnJpY2FuJTIwc3R1ZGVudHMlMjBlZHVjYXRpb258ZW58MXx8fHwxNzYyNDIwMTMxfDA&ixlib=rb-4.1.0&q=80&w=1080"
                                    alt="HSCF Community Impact"
                                    className="hero-image"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <div className="scroll-indicator">
                    <Icon icon="mdi:mouse" />
                    <span>Scroll to explore</span>
                </div>
            </section>

            {/* Mission Section */}
            <section className="mission-section">
                <div className="container">
                    <div className="row">
                        <div className="col-lg-6 mb-4 mb-lg-0">
                            <div className="mission-image-wrapper">
                                <ImageWithFallback
                                    src="https://images.unsplash.com/photo-1641057324798-c05769de628a?ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&q=80&w=1170"
                                    alt="Our Mission"
                                    className="mission-image"
                                />
                                <div className="mission-badge">
                                    <Icon icon="mdi:shield-check" />
                                    <span>Trusted Organization</span>
                                </div>
                            </div>
                        </div>
                        <div className="col-lg-6 px-4">
                            <div className="section-header">
                                <span className="section-label">Our Mission</span>
                                <h2 className="section-title">Nigeria's first indigenous White-Label Non-Profit.</h2>
                            </div>
                            <p className="mission-text">
                                We believe that true, positive, and sustainable change begins within communities; when their knowledge, leadership, ownership, and dreams are tapped into. That is why we work with and through local actors to design and implement solutions that are rooted in local cultures, responsive to local needs, and adaptable to challenging geographic and social realities.
                            </p>
                            <div className="mission-features">
                                <div className="feature-item">
                                    <div className="feature-icon">
                                        <Icon icon="mdi:sparkles" />
                                    </div>
                                    <div className="feature-content">
                                        <h4>Integrity</h4>
                                        <p>We are committed and responsible stewards, and hold ourselves to the highest standards of transparency, accountability, and ethical practice.</p>
                                    </div>
                                </div>
                                <div className="feature-item">
                                    <div className="feature-icon">
                                        <Icon icon="mdi:school" />
                                    </div>
                                    <div className="feature-content">
                                        <h4>Evidence & Learning</h4>
                                        <p>We continuously learn from our experiences and use evidence to refine our strategies and improve outcomes. </p>
                                    </div>
                                </div>
                                <div className="feature-item">
                                    <div className="feature-icon">
                                        <Icon icon="mdi:lightbulb" />
                                    </div>
                                    <div className="feature-content">
                                        <h4>Innovation</h4>
                                        <p>We embrace creativity and new ideas, from grassroots solutions to donor-driven models, as catalysts for health social change. </p>
                                    </div>
                                </div>
                            </div>
                            <Link to="/about" className="btn btn-learn-more">
                                Learn More About Us
                                <Icon icon="mdi:arrow-right" className="ms-2" />
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* Programs Showcase */}
            <section className="programs-showcase">
                <div className="container">
                    <div className="section-header text-center">
                        <span className="section-label">What We Do</span>
                        <h2 className="section-title">Our Flagship Programs</h2>
                        <p className="section-description">
                            Discover our initiatives that are making a real difference in communities
                        </p>
                    </div>

                    <div className="row g-4">
                        <div className="col-lg-4 col-md-6">
                            <div className="program-card">
                                <div className="program-icon">
                                    <Icon icon="mdi:school" />
                                </div>
                                <h3>EducateHer</h3>
                                <p>
                                    Through EducateHer, HSC Foundation is partnering with community leaders, teachers, parents, and local associations to strengthen girls' school attendance, reduce dropout rates, and promote pathways to leadership for young women.
                                </p>
                                <Link to="/programs/educateher" className="program-link">
                                    Learn More <Icon icon="mdi:arrow-right" />
                                </Link>
                                <div className="program-card-bg"></div>
                            </div>
                        </div>

                        <div className="col-lg-4 col-md-6">
                            <div className="program-card">
                                <div className="program-icon">
                                    <Icon icon="mdi:map-marker-path" />
                                </div>
                                <h3>RTYGIA</h3>
                                <p>
                                    The Route Tracers YSN Geospatial Innovation Award is a 5-year endowed initiative designed to identify, support, and scale innovative, scalable, and wide-ranging ideas in the field of surveying, geographic information systems, remote sensing, and environmental management, developed by young surveyors (aged 35 years and below) who are members of the Young Surveyors Network, Nigeria.
                                </p>
                                <Link to="/programs/rtygia" className="program-link">
                                    Learn More <Icon icon="mdi:arrow-right" />
                                </Link>
                                <div className="program-card-bg"></div>
                            </div>
                        </div>

                        <div className="col-lg-4 col-md-6">
                            <div className="program-card">
                                <div className="program-icon">
                                    <Icon icon="mdi:dna" />
                                </div>
                                <h3>Aikpokpodion Fellowship</h3>
                                <p>
                                    Supporting groundbreaking research in genetics and biotechnology
                                    through prestigious fellowship awards.
                                </p>
                                {/*
                                <Link to="/programs/aikpokpodion-fellowship" className="program-link">
                                Learn More <Icon icon="mdi:arrow-right" />
                                </Link>
                                */}
                                <Link to="#" className="program-link">
                                    Coming Soon
                                </Link>
                                <div className="program-card-bg"></div>
                            </div>
                        </div>
                    </div>

                    <div className="text-center mt-5">
                        <Link to="/programs" className="btn btn-view-all">
                            View All Programs
                            <Icon icon="mdi:arrow-right" className="ms-2" />
                        </Link>
                    </div>
                </div>
            </section>

            {/* Stats Section */}
            {/*
            <section className="stats-section" ref={statsRef}>
                <div className="container">
                <div className="row g-4">
                    <div className="col-lg-3 col-md-6">
                    <div className="stat-card">
                        <div className="stat-icon">
                        <Icon icon="mdi:account-group" />
                        </div>
                        <div className="stat-number" data-target="10000">10,000+</div>
                        <div className="stat-label">Lives Impacted</div>
                    </div>
                    </div>
                    <div className="col-lg-3 col-md-6">
                    <div className="stat-card">
                        <div className="stat-icon">
                        <Icon icon="mdi:school" />
                        </div>
                        <div className="stat-number" data-target="500">500+</div>
                        <div className="stat-label">Scholarships Awarded</div>
                    </div>
                    </div>
                    <div className="col-lg-3 col-md-6">
                    <div className="stat-card">
                        <div className="stat-icon">
                        <Icon icon="mdi:earth" />
                        </div>
                        <div className="stat-number" data-target="25">25+</div>
                        <div className="stat-label">Communities Served</div>
                    </div>
                    </div>
                    <div className="col-lg-3 col-md-6">
                    <div className="stat-card">
                        <div className="stat-icon">
                        <Icon icon="mdi:handshake" />
                        </div>
                        <div className="stat-number" data-target="50">50+</div>
                        <div className="stat-label">Partner Organizations</div>
                    </div>
                    </div>
                </div>
                </div>
            </section>
            */}

            {/* CTA Section */}
            <section className="cta-section">
                <div className="container">
                    <div className="cta-card">
                        <div className="row align-items-center">
                            <div className="col-lg-8">
                                <h2 className="cta-title">Ready to Make a Difference?</h2>
                                <p className="cta-text">
                                    Join us in our mission in health improvement and social change. Your support can change someone's future.
                                </p>
                            </div>
                            <div className="col-lg-4 text-lg-end">
                                {/*
                                <button className="btn btn-cta-primary">
                                <Icon icon="mdi:hand-heart" className="me-2" />
                                Donate Now
                                </button>
                                */}
                                <Link to="/contact" className="btn btn-cta-secondary text-white">
                                    Get Involved
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}

export default Home;
